import { addMonths, format, subMonths } from "date-fns";
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Calendar as ReactCalendar } from "react-calendar";
import { IntervalDay } from "../Steps/AppointmentStep/AppointmentStep";
import { CalendarArrow } from "./CalendarArrow/CalendarArrow";
import { nb } from "date-fns/locale";
import { GtmService } from "../GtmService";
import { useSwipeable } from "react-swipeable";
import classNames from "classnames";
import { useLabels } from "../Hooks/useLabels";
import { LoadingAnimation } from "../../LoadingAnimation/LoadingAnimation";

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];
type CalendarTileProps = {
  date: Date;
  view: string;
};

type StyledCalendarProps = {
  selectedDay: Date;
  onChange: (day: Date) => void;
  interval?: IntervalDay[];
  selectedMonth: Date;
  onMonthChange: (month: Date) => void;
  loading?: boolean;
  loadingAppointmentsDetails?: boolean;
  disabled?: boolean;
  setSelectedDay: React.Dispatch<React.SetStateAction<Date>>;
  noAppointmentsTodayLabel?: string;
  firstAvailableDateButton?: string;
  firstAvailableDate: Date | null;
  noAppointmentSelectedError?: boolean;
  setNoAppointmentSelectedError?: (value: boolean) => void;
  autoQueryRepeat: number;
  activeMonth: Date;
};

export const Calendar: FC<StyledCalendarProps> = ({
  selectedDay,
  onChange,
  interval = [],
  selectedMonth,
  onMonthChange,
  loading = false,
  loadingAppointmentsDetails = false,
  disabled = false,
  setSelectedDay,
  noAppointmentsTodayLabel,
  firstAvailableDateButton,
  firstAvailableDate,
  noAppointmentSelectedError,
  setNoAppointmentSelectedError,
  autoQueryRepeat,
  activeMonth,
}) => {
  const daysRef = useRef<HTMLDivElement | null>(null);
  const dragOffsetRef = useRef(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const { bookingLabels } = useLabels();

  const defaultFirstAvailableDateButton = "Hopp til neste ledige dato ($date)";
  const defaultNoAppointmentsTodayLabel = "Ingen ledige timer i dag";

  const handleDateChange = (newValue: Value) => {
    if (noAppointmentSelectedError) {
      setNoAppointmentSelectedError?.(false);
    }
    if (newValue instanceof Date) {
      onChange(newValue);
    }
  };

  const dateFormat = "EEEE d. MMMM";

  const hasAppointments = (date: Date): boolean => {
    if (interval.length === 0) return false;
    const intervalDay = interval.find(
      (day) => day.date.toDateString() === date.toDateString()
    );
    return intervalDay?.hasAppointments ?? false;
  };

  useEffect(() => {
    if (!loading && !hasAppointments(selectedDay)) {
      GtmService.noAppointmentsToday();
    }
  }, [selectedDay, hasAppointments, loading]);

  useEffect(() => {
    GtmService.appointmentDateSelected(selectedDay);
  }, [selectedDay]);

  const firstAvailableDateButtonText = (
    firstAvailableDateButton
      ? firstAvailableDateButton
      : defaultFirstAvailableDateButton
  ).replace(
    "$date",
    firstAvailableDate
      ? format(firstAvailableDate, dateFormat, { locale: nb })
      : "ingen dato"
  );

  useLayoutEffect(() => {
    const daysEl = document.querySelector<HTMLDivElement>(
      ".react-calendar__month-view__days"
    );
    if (daysEl) daysRef.current = daysEl;
  }, [selectedMonth]);

  const handleActiveStartDateChange = ({
    action,
  }: {
    action: "prev" | "next";
  }) => {
    if (action === "prev") {
      onMonthChange(subMonths(selectedMonth, 1));
    }
    if (action === "next") {
      onMonthChange(addMonths(selectedMonth, 1));
    }
    return;
  };

  const handlers = useSwipeable({
    onSwiping: ({ deltaX }) => {
      if (loading) return;
      setIsSwiping(true);
      dragOffsetRef.current = deltaX;
      requestAnimationFrame(() => {
        if (daysRef.current)
          daysRef.current.style.transform = `translateX(${dragOffsetRef.current}px)`;
      });
    },
    onSwiped: ({ dir }) => {
      setIsSwiping(false);
      const threshold = 100;
      if (dir === "Left" && Math.abs(dragOffsetRef.current) > threshold) {
        onMonthChange(addMonths(selectedMonth, 1));
      } else if (
        dir === "Right" &&
        Math.abs(dragOffsetRef.current) > threshold
      ) {
        const today = new Date();
        const prevMonth = subMonths(selectedMonth, 1);
        if (prevMonth >= new Date(today.getFullYear(), today.getMonth(), 1)) {
          onMonthChange(prevMonth);
        }
      }

      if (daysRef.current) {
        daysRef.current.style.transition = "transform 0.2s ease-out";
        daysRef.current.style.transform = "translateX(0)";
      }
      dragOffsetRef.current = 0;

      setTimeout(() => {
        if (daysRef.current) daysRef.current.style.transition = "none";
      }, 200);
    },
  });

  function* countdown(a: number, b: number = 1) {
    while (a >= b) {
      yield a--;
    }
  }

  return (
    <div
      className="calendar-container"
      role="group"
      aria-label="Kalender for valg av dato"
      aria-busy={loading}
    >
      <div
        {...handlers}
        className={classNames("calendar-swipe-wrapper", {
          "calendar-swipe-wrapper--swiping": isSwiping,
        })}
      >
        <ReactCalendar
          onChange={handleDateChange}
          value={hasAppointments(selectedDay) ? selectedDay : null}
          onActiveStartDateChange={handleActiveStartDateChange}
          locale="nb-NO"
          activeStartDate={selectedMonth}
          formatDay={(locale: string, date: Date) => date.getDate().toString()}
          formatShortWeekday={(locale: string, date: Date) => {
            const days = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"];
            return days[date.getDay()];
          }}
          showNeighboringMonth={true}
          next2Label={null}
          prev2Label={null}
          nextLabel={
            <CalendarArrow
              direction="next"
              ariaLabel="Neste måned"
              loading={loading}
            />
          }
          prevLabel={
            <CalendarArrow
              direction="prev"
              ariaLabel="Forrige måned"
              loading={loading}
            />
          }
          tileDisabled={({ date, view }: CalendarTileProps) => {
            if (view === "month" && !loading && !disabled)
              return !hasAppointments(date);
            return loading || disabled;
          }}
          tileClassName={({ date, view }: CalendarTileProps) => {
            if (view === "month")
              return hasAppointments(date)
                ? "tile-has-appointment"
                : "tile-no-appointment";
            return "";
          }}
          minDate={new Date()}
          minDetail="month"
          maxDetail="month"
          className={loading ? "calendar--loading" : "calendar"}
        />

        {loading && (
          <div className="calendar-loading-overlay">
            <div className="spinner" />
            <div className="loading-text">
              <LoadingAnimation
                text={
                  autoQueryRepeat === 0
                    ? bookingLabels!.stepsBookingLabels.secondStepLabels.loadingAppointmentsForCurrentMonth.replace(
                        "$currentMonth",
                        format(activeMonth, "MMMM", { locale: nb })
                      )
                    : bookingLabels!.stepsBookingLabels.secondStepLabels.loadingAppointmentsForNextMonth
                        .replace(
                          "$currentMonth",
                          format(activeMonth, "MMMM", { locale: nb })
                        )
                        .replace(
                          "$checkedMonths",
                          [...countdown(autoQueryRepeat)]
                            .map((value) =>
                              format(subMonths(activeMonth, value), "MMMM", {
                                locale: nb,
                              })
                            )
                            .join(", ")
                        )
                }
                className="loading-animation-container text-center"
              />
            </div>
          </div>
        )}
      </div>
      {firstAvailableDateButtonText &&
        !loadingAppointmentsDetails &&
        !loading && (
          <div className="no-appointments">
            <h2>{format(selectedDay, dateFormat, { locale: nb })}</h2>

            {!hasAppointments(selectedDay) && (
              <>
                <p>
                  {noAppointmentsTodayLabel ?? defaultNoAppointmentsTodayLabel}
                </p>
                <button
                  onClick={() => {
                    GtmService.firstAvailableDateSelected();
                    const dateToSelect = firstAvailableDate ?? new Date();
                    setSelectedDay(dateToSelect);
                    onMonthChange(
                      new Date(
                        dateToSelect.getFullYear(),
                        dateToSelect.getMonth(),
                        1
                      )
                    );
                  }}
                >
                  {firstAvailableDateButtonText}
                </button>
              </>
            )}
          </div>
        )}
    </div>
  );
};
